//////////////////////////////////////////////////
/// AREA: ソースインポート
//////////////////////////////////////////////////

// 独自モジュール
import { FadeInEffect } from './modules/animation/_Fade';
import { SlideController } from './modules/animation/_Slide';
import { ClickEventHandler } from './modules/dom/_ClickEvent';
import { ClassListController } from './modules/dom/_ClassProcessor';
import { StyleController } from './modules/dom/_CssProperty';
import { GraphicsLazyController } from './modules/media/_Graphics';

// サイト機能
import { ModalDisplay } from './features/ModalWindow';
import { CollapsePanel } from './features/Collapse';
import { TabSwitcher } from './features/TabSwitch';
import { SlideDisplay } from './features/SlideShow';

//////////////////////////////////////////////////
/// AREA: グローバル変数
//////////////////////////////////////////////////

const gSiteBody: HTMLElement = document.body;
const gLazyTargets = [
  {
    selector: '.lazy_bg_image',
    options: {}
  },
  {
    selector: '.lazy_iframe',
    options: {
      rootMargin: '480px',
    }
  }
];

const gLazyController: GraphicsLazyController = new GraphicsLazyController(gLazyTargets);

let gScrollFlag: boolean = false;
let gResizeFlag: boolean = false;

//////////////////////////////////////////////////
/// AREA: トップページ
//////////////////////////////////////////////////

/// Part Name: ヒーロービュー
const frontHeroView = {
  INIT_OPACITY: 1,
  MIN_SCALE: 1,
  MAX_SCALE: 1.24,
  MAX_POSITION: 960,
  INTERVAL_OPACITY: 800,
  INTERVAL_SCALE: 2400,
  // mVisual: document.getElementById('primaryHeroView')?.querySelector('.visual')?.querySelector('.image') as HTMLElement | null,
  mVisual: document.getElementById('primaryHeroView')?.querySelector('.visual')?.querySelector('.bg_image') as HTMLElement | null,
  mWindowHeight: window.innerHeight,
  StyleController: new StyleController(),

  scrollAnimation(): void
  {
    const elem_style: CSSStyleDeclaration | undefined = this.mVisual?.style;

    this.applyFadeOut(elem_style);
    // mVisualがbackground-image: background, img要素: object を指定
    // this.applyPosition(elem_style, 'background');
    // スクロール時大きく: increase, 小さく: decrease
    this.applyScale(elem_style, 'decrease');
  },

  applyFadeOut(style: CSSStyleDeclaration | undefined): void
  {
    const value: number = this.INIT_OPACITY - window.scrollY / this.INTERVAL_OPACITY;

    this.StyleController.clampValue(style, 'opacity', `${value}`, 0, this.INIT_OPACITY);
  },

  applyPosition(style: CSSStyleDeclaration | undefined, property: string = 'object'): void
  {
    const inset_posY: number = window.scrollY;
    const elem_posY: number = - window.scrollY / 2;

    this.StyleController.clampValue(style, 'inset-block-start', `${inset_posY}px`, 0, this.MAX_POSITION,);

    if (property === 'object')
    {
      const prop_pos: string = 'object-position';
      this.StyleController.clampValue(style, prop_pos, `bottom ${elem_posY}px`, - this.MAX_POSITION / 2, 0);
    }
    else if (property === 'background')
    {
      const prop_pos: string = 'background-position-y';
      this.StyleController.clampValue(style, prop_pos, `${elem_posY}px`, - this.MAX_POSITION / 2, 0,);
    }
  },

  applyScale(style: CSSStyleDeclaration | undefined, direction: 'increase' | 'decrease'): void
  {
    const value: number =
      direction === 'increase'
        ? this.MIN_SCALE + window.scrollY / this.INTERVAL_SCALE
        : this.MAX_SCALE - window.scrollY / this.INTERVAL_SCALE;

    this.StyleController.clampValue(style, 'scale', `${value}`, this.MIN_SCALE, this.MAX_SCALE);
  },
};

//////////////////////////////////////////////////
/// AREA: Webサイト共通
//////////////////////////////////////////////////

/// Part Name: スクロールエフェクト
const gScrollFadeIn = {
  fromLeft: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('from_left')),
    onAnimation(): void
    {
      // IntersectionObserver を使ってフェードインを登録
      this.FadeInEffect.observe('fadeInFromLeft');
    }
  },
  fromBottom: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('from_bottom')),
    onAnimation(): void
    {
      this.FadeInEffect.observe('fadeInFromBottom');
    }
  },
  slideDoor: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('slide_door')),
    onAnimation(): void
    {
      this.FadeInEffect.observe('fadeInSlideDoor');
    }
  }
};

/// Part Name: グローバルナビ
const gHeaderNavi = {
  ClickEventHandler: new ClickEventHandler(),
  mIcon: document.getElementById('gHeaderNaviIcon'),
  mNavi: document.getElementById('gHeaderNavi'),
  mNaviItems: null as HTMLCollectionOf<Element> | null,
  mToggleClassName: 'is_active',

  main(): void
  {
    if (this.clickToToggleDisplay)
    {
      this.clickToToggleDisplay();
    }
    if (this.clickToCloseDisplay)
    {
      this.clickToCloseDisplay();
    }
  },
  clickToToggleDisplay(): void
  {
    if (this.mIcon && this.mNavi)
    {
      this.ClickEventHandler.mTrigger = this.mIcon;
      this.ClickEventHandler.mTarget = this.mNavi;
      this.ClickEventHandler.toggleClassOnTarget(this.mToggleClassName);
    }
  },

  clickToCloseDisplay(): void
  {
    if (this.mNavi)
    {
      this.mNaviItems = this.mNavi.getElementsByClassName('item');
      for (const item of this.mNaviItems)
      {
        item.addEventListener('click', () =>
        {
          if (this.mIcon?.classList.contains(this.mToggleClassName) && this.mNavi?.classList.contains(this.mToggleClassName))
          {
            this.mIcon.classList.remove(this.mToggleClassName);
            this.mNavi.classList.remove(this.mToggleClassName);
          }
        });
      }
    }
  }
};

/// Part Name: サイドナビ
const gSideNavi = {
  BREAK_POINT: 1201,
  mNavi: document.getElementById('gSideNavi'),

  scrollFixed(): void
  {
    if (this.mNavi && window.innerWidth >= this.BREAK_POINT)
    {
      const navi_rect: DOMRect = this.mNavi.getBoundingClientRect();
      const navi_height: number = navi_rect.height;
      const move_posY: string = 'calc(50% - ' + navi_height / 2 + 'px)';
      const navi_top: number = window.scrollY + navi_rect.top;

      if (window.scrollY > navi_top / 2)
      {
        this.mNavi.style.position = 'fixed';
        this.mNavi.style.insetBlockEnd = move_posY;
      }
      else
      {
        this.mNavi.style.position = 'absolute';
        this.mNavi.style.removeProperty('inset-block-end');
      }
    }
  }
};

/// Part Name: バックスクロールボタン
const gScrollTopBtn = {
  ClassListController: new ClassListController(),
  ClickEventHandler: new ClickEventHandler(),
  mButton: document.getElementById('gScrollTopBtn'),
  isDisplayed: false,
  onAnimation(target_height = 200): void
  {
    this.ClassListController.mTarget = this.mButton;
    const should_display: boolean = window.scrollY > target_height;

    if (should_display !== this.isDisplayed)
    {
      this.isDisplayed = should_display;
      if (should_display)
      {
        if (this.mButton) this.mButton.style.visibility = 'visible';
        this.ClassListController.switchClass('fadeOutDisp', 'fadeInDisp');
      }
      else
      {
        this.ClassListController.switchClass('fadeInDisp', 'fadeOutDisp');
      }
    }
  },

  toPageTop(): void
  {
    this.ClickEventHandler.mTrigger = this.mButton;
    this.ClickEventHandler.scrollToPageTop();
  }
};

/// Parts Name: 関連サービスボタン
const shop_button = document.querySelectorAll('.g-btn-related');

for (const button of shop_button)
{
  // button.addEventListener('click', () =>
  // {
  //   window.open(button.dataset.url, '_blank');
  // });
  if (button instanceof HTMLElement)
  {
    let scroll_timer: number | null = null;

    window.addEventListener('scroll', () =>
    {
      button.classList.add('is_hidden');

      if (scroll_timer !== null)
      {
        window.clearTimeout(scroll_timer);
      }

      scroll_timer = window.setTimeout(() =>
      {
        button.classList.remove('is_hidden');
      }, 400);
    });
  }
}

/// Part Name: コピーライト
const gCopyright = {
  SINCE_YEAR: 2025,
  mDispYear: document.getElementById('dispYear'),
  iDate: new Date(),

  getNowYear(): number
  {
    const now_year: number = this.iDate.getFullYear();
    return now_year;
  },

  dispYear(): void
  {
    if (this.mDispYear)
    {
      if (this.SINCE_YEAR === this.getNowYear())
      {
        this.mDispYear.innerHTML += this.SINCE_YEAR;
      }
      else
      {
        this.mDispYear.innerHTML += `${this.SINCE_YEAR} - ${this.getNowYear()}`;
      }
    }
  }
};

//////////////////////////////////////////////////
/// AREA: debugコード
//////////////////////////////////////////////////

const MEDIA_GALLERY_PC_LIMIT = 14;
const MEDIA_GALLERY_SP_LIMIT = 16;
const MEDIA_GALLERY_SP_MEDIA_QUERY = '(max-width: 768px)';
const ATTR_MODAL_CONTENT_DISABLED = 'data-modal-content-disabled';

const getMediaGalleryDisplayLimit = (): number =>
{
  return window.matchMedia(MEDIA_GALLERY_SP_MEDIA_QUERY).matches
    ? MEDIA_GALLERY_SP_LIMIT
    : MEDIA_GALLERY_PC_LIMIT;
};

const applyMediaGalleryDisplayLimit = (root: HTMLElement): void =>
{
  const display_limit = getMediaGalleryDisplayLimit();

  const content_items = Array.from(
    root.querySelectorAll<HTMLElement>('[data-modal-content-item]')
  );

  content_items.forEach((content_item: HTMLElement) =>
  {
    const group_number = Number(content_item.dataset.galleryGroupNumber);

    if (!Number.isFinite(group_number))
    {
      return;
    }

    content_item.toggleAttribute(
      ATTR_MODAL_CONTENT_DISABLED,
      group_number > display_limit,
    );
  });
};

const modalWindow = {
  mMediaGallery: {
    root: document.getElementById('areaPrimaryMediaGallery'),
    mount(): void
    {
      const root = this.root;

      if (!root)
      {
        return;
      }

      applyMediaGalleryDisplayLimit(root);

      modalWindow.setup(root, (modal: ModalDisplay) =>
      {
        modal.runProgram();
      });
    },
  },
  setup(root: HTMLElement | null, bind: (modal: ModalDisplay) => void): void
  {
    if (!root)
    {
      return;
    }

    const modal = new ModalDisplay(gSiteBody, root);
    bind(modal);
  }
};

document.addEventListener('DOMContentLoaded', () =>
{
  modalWindow.mMediaGallery.mount();
});


//////////////////////////////////////////////////
/// AREA: イベント、メソッドの実行
//////////////////////////////////////////////////
const SlideShow = new SlideDisplay(document.getElementById('slideHeroView'));

window.addEventListener('load', () =>
{
  // gModal.runProgram();
  gLazyController.loadGraphics();
  if (gHeaderNavi.clickToToggleDisplay)
  {
    gHeaderNavi.clickToToggleDisplay();
  }
  if (gHeaderNavi.clickToCloseDisplay)
  {
    gHeaderNavi.clickToCloseDisplay();
  }

  gSideNavi.scrollFixed();
  gScrollTopBtn.toPageTop();
  gScrollTopBtn.onAnimation();
  gScrollFadeIn.fromLeft.onAnimation();
  gScrollFadeIn.fromBottom.onAnimation();
  gScrollFadeIn.slideDoor.onAnimation();
  gCopyright.dispYear();

  // frontHeroView.scrollAnimation();
  SlideShow.startAutoSlide();
});

window.addEventListener('scroll', () =>
{
  if (!gScrollFlag)
  {
    window.requestAnimationFrame(() =>
    {
      // gSideNavi.scrollFixed();
      gScrollTopBtn.onAnimation();
      // frontHeroView.scrollAnimation();

      gScrollFlag = false;
    });
    gScrollFlag = true;
  }
});

window.addEventListener('resize', () =>
{
  if (!gResizeFlag)
  {
    window.requestAnimationFrame(() =>
    {
      // gSideNavi.scrollFixed();
      gScrollTopBtn.onAnimation();
      // frontHeroView.scrollAnimation();

      gScrollFlag = false;
    });
    gResizeFlag = true;
  }
});

//////////////////////////////////////////////////
/// AREA: END
//////////////////////////////////////////////////
